import { Checkbox } from "@/components/ui/checkbox";

export type PermissionOption = {
  code: string;
  name: string;
  description: string | null;
  category: string;
  categoryLabel?: string;
};

interface PermissionsSelectorProps {
  permissions: PermissionOption[];
  selectedCodes: string[];
  onTogglePermission: (permissionCode: string) => void;
  onToggleCategory: (permissionCodes: string[]) => void;
  isDisabled?: boolean;
}

export function PermissionsSelector({
  permissions,
  selectedCodes,
  onTogglePermission,
  onToggleCategory,
  isDisabled = false,
  readOnly = false,
}: PermissionsSelectorProps & { readOnly?: boolean }) {
  const categoryTranslations: Record<string, string> = {
    users: "Пользователи",
    "admin-users": "Администраторы",
    roles: "Роли",
    permissions: "Права доступа",
    content: "Контент",
    settings: "Настройки",
    finance: "Финансы",
    support: "Поддержка",
    crm: "CRM",
    analytics: "Аналитика",
    system: "Система",
    marketing: "Маркетинг",
    communications: "Коммуникации",
    "subscription-plans": "Абонементы",
    subscriptions: "Владельцы абонементов",
    products: "Товары",
    events: "События",
    orders: "Заказы",
    "knowledge-base": "База знаний",
    "knowledge-documents": "База знаний (Документы)",
    "support-tickets": "Обращения",
    broadcasts: "Рассылки",
    payments: "Платежи",
    "daily-stats": "Дневная статистика",
    courses: "Курсы",
    banners: "Баннеры",
    articles: "Статьи",
    announcements: "Анонсы",
    teachers: "Учителя",
    "telegram-channels": "Telegram каналы",
    triggers: "Триггеры",
    languages: "Языки",
    localization: "Локализация",
    ai: "AI Manager",
  };

  const getCategoryLabel = (cat: string, label?: string) => {
    if (label) {return label;}
    return categoryTranslations[cat.toLowerCase()] || cat;
  };

  const grouped = permissions.reduce(
    (acc: Record<string, PermissionOption[]>, perm) => {
      const category = getCategoryLabel(perm.category, perm.categoryLabel);
      if (!acc[category]) {acc[category] = [];}
      acc[category].push(perm);
      return acc;
    },
    {}
  );

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([category, perms]) => {
        const categoryCodes = perms.map((perm) => perm.code);
        const allSelected = categoryCodes.every((code) => selectedCodes.includes(code));
        return (
          <div key={category} className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-border/70">
              <h3 className="text-lg font-medium">{category}</h3>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onToggleCategory(categoryCodes)}
                  disabled={isDisabled}
                  className="text-sm font-medium text-primary hover:text-primary/80 disabled:cursor-not-allowed disabled:text-muted-foreground"
                >
                  {allSelected ? "Снять все" : "Выбрать все"}
                </button>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {perms.map((perm) => (
                <div key={perm.code} className="flex items-start gap-2">
                  <Checkbox
                    id={`perm-${perm.code}`}
                    checked={selectedCodes.includes(perm.code)}
                    onCheckedChange={() =>
                      !readOnly && !isDisabled && onTogglePermission(perm.code)
                    }
                    disabled={isDisabled || readOnly}
                  />
                  <label
                    htmlFor={`perm-${perm.code}`}
                    className="text-sm cursor-pointer"
                  >
                    <span className="font-medium">{perm.name}</span>
                    {perm.description && (
                      <span className="block text-muted-foreground text-xs">
                        {perm.description}
                      </span>
                    )}
                  </label>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
